import React, { useCallback, useState } from 'react';
import { Table, Space, Spin } from 'antd';
import { 
  TableWrapper, 
  B2cPmlEditModal, 
  BillB2CRequestModal } from 'components/molecules';
import { usePopupManager } from 'react-popup-manager';
import { ColumnsType } from 'antd/lib/table/interface';
import { TotalNumMenu } from 'components/atoms';
import { useLocation, useNavigate } from 'react-router';
import { addNewSearch } from 'utils/utils';
import { useTableInfo } from 'hooks';
import { INIT_PAGESIZE } from 'commons/constants';
import qs from 'query-string';
import * as XLSX from 'xlsx';
import { fetcher } from 'services';

interface FormItemProps {
  title: string | undefined;  
  pmlList: B2cPmlListDto[] | undefined;  
  searchResult: any;
  hiddenColumns: string[];
  register: boolean;
  useExcelDownload?: boolean;
  refetch: () => void;
}

const PMLTable = ({title, pmlList, searchResult, hiddenColumns, register, useExcelDownload = false, refetch}: FormItemProps): React.ReactElement => {
  /* ======================================================
   * variables
   * ====================================================== */
  const navigate = useNavigate();
  const location = useLocation();
  const { open } = usePopupManager();
  const [isLoding, setIsLoading] = useState<boolean>(false);   
  const searchParams = qs.parse(location.search);
  const columns: ColumnsType<B2cPmlListDto> = [
    {
      title: '번호',
      dataIndex: 'id',
      key: 'id',
      width: '70px',
      align: 'center',
    },  
    {
      title: '구분',
      dataIndex: 'close_yn',
      key: 'close_yn',
      width: '70px',
      align: 'center',
      sorter: (a, b) => a.close_yn.localeCompare(b.close_yn),
      render: (close_yn, record) => {
        return (
          record.status_type==='drop'?'폐강':close_yn==='Y'?'마감':'모집중'
        )
      }       
    },      
    {
      title: '진단일정',
      key: 'diagnose',
      width: '200px',
      align: 'center',
      render: (record) => {
        return (
          <label>{record.start_date} ~ {record.end_date}</label> 
        )
      }       
    },     
    {
      title: '과정명',
      dataIndex: 'title',
      key: 'title',
      width: '200px',
      align: 'left', 
      ellipsis: true,    
    },  
    {
      title: '디브리핑',
      key: 'debriefing',
      width: 'auto',
      align: 'center',
      ellipsis: true,
      render: (record) => {
        return (
          <label>{record.debriefing_date}&nbsp;&nbsp;{record.debriefing_start_time} ~ {record.debriefing_end_time}&nbsp;&nbsp;{record.lecturer}</label> 
        )
      } 
    },
    {
      title: '영상학습일',
      key: 'study',
      width: 'auto',
      align: 'center',
      render: (record) => {
        return (
          <label>{record.study_start_date} ~ {record.study_end_date}</label> 
        )
      }        
    },   
    {
      title: '미발행매출',
      key: 'not_publish_pirce',
      dataIndex: 'not_publish_pirce',
      width: '120px',
      align: 'right',
      ellipsis: true,
      sorter: (a, b) => a.not_publish_pirce - b.not_publish_pirce, 
      onCell: (record) => {
        return {
            onClick: (e) => {
              e.stopPropagation();
              onBillRequestPopup(record.id);
            },
        };
      }, 
      render: (not_publish_pirce, record) => {
       return (
        <span>{not_publish_pirce.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
       )
     } 
    },      
   {
    title: '매출금액',
    key: 'max_price',
    dataIndex: 'max_price',
    width: '120px',
    align: 'right',
    sorter: (a, b) => a.max_price - b.max_price,    
    render: (max_price, record) => {
     return (
      max_price && max_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
     )
   }      
  },  
  {
    title: '예상매출',
    dataIndex: 'expect_price',
    key: 'expect_price',
    width: '120px',
    align: 'right',
    ellipsis: true,
    sorter: (a, b) => a.expect_price - b.expect_price, 
    render: (expect_price, record) => {
     return (
       expect_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
     )
   }       
  },   
   {
    title: 'HSG 담당자',
    dataIndex: 'pm',
    key: 'pm',
    width: '120px',
    align: 'center',
    ellipsis: true,    
  }, 
    {
     title: '진단인원',
     dataIndex: 'student_cnt',
     key: 'student_cnt',
     width: '80px',
     align: 'center',
     ellipsis: true,
     onCell: (record) => {
      return {
          onClick: (e) => {
            e.stopPropagation();
            window.location.href = "/b2c/pml/student/" + record.id;
          },
      };     
   },
  }
  ];

  /* ======================================================
  * events
  * ====================================================== */ 

  const onClickRegister = () => {
    open(B2cPmlEditModal, {id: 0, onClose: (isOk)=>{if(isOk) {refetch();}}}); 
 }

 const onChangePageInfo = (pageNo: number, pageSize: number) => {
   navigate({
     ...location,
     search: addNewSearch(location.search, { pageNo, pageSize }),
   });
 };  

 const onRowClick = useCallback((record, index) => {
  return {
    onClick: () => {
      open(B2cPmlEditModal, {id: Number(record.id), onClose: (isOk)=>{if(isOk) {refetch();}}});         
    },
  };
 }, []);

 const onBillRequestPopup = (id: number) => {
  open(BillB2CRequestModal, {
    id: id,
    student_id: 0,
    onClose: (isOk) => {
      if (isOk) {
        refetch();
      }
    },
  }); 
 }

 const onExcelDownload = () => {
  setIsLoading(true); 
  fetcher.b2c.postProjectPmlExcelList({
    pageNo: 1,
    pageSize: 0,
    searchValue: searchParams.search ? String(searchParams.search) : '',  
    searchDateStart: searchParams.searchDateStart ? String(searchParams.searchDateStart) : '',   
    searchDateEnd: searchParams.searchDateEnd ? String(searchParams.searchDateEnd) : '',  
    pm: searchParams.pm ? String(searchParams.pm) : '',  
    company: searchParams.company ? String(searchParams.company) : '',   
    crm: searchParams.crm ? String(searchParams.crm) : '',
  })
  .then((res) => {
      switch (res.code) {
          case 200:

          if (res.result) {
            const book = XLSX.utils.book_new();
            const nurses = XLSX.utils.json_to_sheet(
              (res.result.list as PmlExcelListDto[]).map((item, index) => {
                return {
                  '번호' : item.project_id,
                  '구분' : item.close_yn,
                  '과정명': item.project_title,
                  '진단 기간': item.start_date?item.start_date+''+'~'+item.end_date:'',
                  '디브리핑': item.debriefing_date?item.debriefing_date + ' '+item.debriefing_start_time+'~'+item.debriefing_end_time + ' ' + item.lecturer: '', 
                  '영상학습 기간': item.study_start_date?item.study_start_date+''+'~'+item.study_end_date:'',
                  '미발행매출': item.not_publish_pirce,
                  '매출금액': item.max_price,
                  '예상매출': item.expect_price,
                  'HSG 담당자': item.pm,
                  '진단인원': item.student_cnt,
                }
              }), 
              { 
                header : [
                  '번호',
                  '구분',
                  '과정명',
                  '진단 기간',
                  '디브리핑',
                  '영상학습 기간',
                  '미발행매출',
                  '매출금액',
                  '예상매출',
                  'HSG 담당자',
                  '진단인원',
                ], 
                skipHeader : false 
              } 
            );
        
            nurses["!cols"] = [
              { wpx : 60 } 
            , { wpx : 80 } 
            , { wpx : 240 }  
            , { wpx : 150 }  
            , { wpx : 200 } 
            , { wpx : 150 } 
            , { wpx : 120 } 
            , { wpx : 120 } 
            , { wpx : 120 } 
            , { wpx : 80 } 
            , { wpx : 80 }
            ]
        
            XLSX.utils.book_append_sheet( book, nurses, "NURSES" );
        
            XLSX.writeFile( book, "성과백신.xlsx" ); 
          }

          break;
          default:  
          break;
      };
      setIsLoading(false);         
  });
 }  

  /* ======================================================
  * hooks
  * ====================================================== */
  const { pagination, onTableChange } = useTableInfo({
    pageInfo: {
      pageNo: Number(searchParams.pageNo) || 1,
      pageSize: Number(searchParams.pageSize) || INIT_PAGESIZE,
    },
    datas: pmlList,
    totalCount: searchResult.totalCount,
    onChangePageInfo,
  });

  /* ======================================================
   * render items
   * ====================================================== */
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Spin spinning={isLoding} tip="처리중입니다. 잠시만 기다려 주세요." size="large">
        <TotalNumMenu text={title} totalNum={searchResult.totalCount} useExcelDownload={useExcelDownload} onDownloadExcel={onExcelDownload} useRegister={register} onUseRegister={onClickRegister}/>
        <TableWrapper isSearch={!!searchParams.search || !!searchParams.lecturer_type || !!searchParams.lecturer_category}>
          <Table
            tableLayout="fixed"
            dataSource={pmlList}
            columns={columns.filter((item) => hiddenColumns.findIndex((col)=>col === item.key) < 0)}
            pagination={pagination}
            onChange={onTableChange}
            onRow={onRowClick}
            style={{ width: '100%', overflowX: 'auto', minWidth: 600 }}
            className="columnCenterFixTable clickable"
            showSorterTooltip={false}
            rowKey={'id'}
          />        
        </TableWrapper>  
      </Spin>
    </Space>
  );
};

export default PMLTable;
